import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";
import { SEO } from "@/components/layout/SEO";
import { fadeInUp, staggerContainer } from "@/lib/motion";
import { Car, City, MapPin, Leaf, Truck, BrainResearch, NavArrowRight } from "iconoir-react";
import { Link } from "react-router-dom";

export default function Services() {
  const { t } = useLanguage();

  const services = [
    {
      icon: Car,
      title: t("Planification de la Mobilité", "Mobility Planning"),
      desc: t("Nous développons des cadres de mobilité stratégiques pour les villes, les régions et les grands corridors de transport, en optimisant l'efficacité, l'équité et la durabilité.", "We develop strategic mobility frameworks for cities, regions, and major transport corridors, optimizing for efficiency, equity, and sustainability."),
      features: [
        t("Plans de Mobilité Urbaine (PMU)", "Urban Mobility Plans (UMP)"),
        t("Études d'Impact sur le Trafic", "Traffic Impact Assessments"),
        t("Stratégies de Mobilité Active", "Active Mobility Strategies"),
        t("Conception de Réseaux de Transport Public", "Public Transport Network Design")
      ]
    },
    {
      icon: City,
      title: t("Infrastructures de Transport", "Transport Infrastructure"),
      desc: t("Ingénierie, conseil et études de faisabilité pour les systèmes d'infrastructures routières, ferroviaires et multimodales.", "Engineering, advisory, and feasibility studies for road, rail, and multimodal infrastructure systems."),
      features: [
        t("Études de Faisabilité de Corridors", "Corridor Feasibility Studies"),
        t("Conception de Carrefours et de Nœuds", "Intersection & Node Design"),
        t("Planification de Pôles Multimodaux", "Multimodal Hub Planning"),
        t("Audits de Capacité des Infrastructures", "Infrastructure Capacity Audits")
      ]
    },
    {
      icon: MapPin,
      title: t("SIG & Intelligence Territoriale", "GIS & Territorial Intelligence"),
      desc: t("Analyse spatiale avancée, cartographie et diagnostics territoriaux basés sur les données pour éclairer les décisions complexes en matière de planification.", "Advanced spatial analysis, mapping, and data-driven territorial diagnostics to inform complex planning decisions."),
      features: [
        t("Infrastructure de Données Spatiales", "Spatial Data Infrastructure"),
        t("Tableaux de Bord WebGIS", "WebGIS Dashboards"),
        t("Diagnostics Territoriaux", "Territorial Diagnostics"),
        t("Analyse de Sélection de Sites", "Site Selection Analysis")
      ]
    },
    {
      icon: Leaf,
      title: t("Durabilité & ESG", "Sustainability & ESG"),
      desc: t("Stratégies environnementales, modélisation de la réduction des émissions et cadres de conformité ESG pour les projets de transport.", "Environmental strategies, emissions reduction modeling, and ESG compliance frameworks for transport projects."),
      features: [
        t("Modélisation de Zones à Faibles Émissions (ZFE)", "Low-Emission Zone (ZFE) Modeling"),
        t("Analytique de l'Empreinte Carbone", "Carbon Footprint Analytics"),
        t("Plans d'Adaptation au Climat", "Climate Adaptation Plans"),
        t("Rapports ESG", "ESG Reporting")
      ]
    },
    {
      icon: Truck,
      title: t("Logistique Urbaine", "Urban Logistics"),
      desc: t("Optimisation du mouvement des marchandises dans les environnements urbains, du dernier kilomètre aux pôles logistiques métropolitains.", "Optimizing the movement of goods in urban environments, from last-mile delivery to metropolitan logistics hubs."),
      features: [
        t("Stratégies de Logistique Urbaine", "City Logistics Strategies"),
        t("Centres de Micro-consolidation", "Micro-consolidation Centers"),
        t("Analyse de Corridors de Fret", "Freight Corridor Analysis"),
        t("Gestion de l'Espace de Livraison", "Curbside Management")
      ]
    },
    {
      icon: BrainResearch,
      title: t("IA & Analytique", "AI & Analytics"),
      desc: t("Déploiement de la vision par ordinateur, de l'intelligence du trafic et de l'analytique prédictive pour opérationnaliser les données de mobilité.", "Deploying computer vision, traffic intelligence, and predictive analytics to operationalize mobility data."),
      features: [
        t("Comptage de Trafic par Vision par Ordinateur", "Computer Vision Traffic Counting"),
        t("Matrices Origine-Destination", "Origin-Destination Matrices"),
        t("Modélisation Prédictive de la Congestion", "Predictive Congestion Modeling"),
        t("Jumeaux Numériques", "Digital Twins")
      ]
    }
  ];

  return (
    <>
      <SEO 
        title={t("Services & Expertise", "Services & Expertise")}
        description={t(
          "Découvrez nos services d'ingénierie : planification de la mobilité, infrastructures de transport, SIG, durabilité et analyses par IA.",
          "Explore our engineering services: mobility planning, transport infrastructure, GIS, sustainability, and AI-powered analytics."
        )}
      />
      <PageHeader 
        overlineFr="Notre Expertise"
        overlineEn="Our Expertise"
        titleFr="Services Intégrés pour des Systèmes Complexes."
        titleEn="Integrated Services for Complex Systems."
        descriptionFr="De la planification stratégique à l'intelligence numérique, nous fournissons des services complets de conseil et d'ingénierie pour les secteurs de la mobilité et de l'aménagement du territoire."
        descriptionEn="From strategic planning to digital intelligence, we provide end-to-end consulting and engineering services for the mobility and territorial sectors."
      />

      <section className="py-20 lg:py-32 bg-warm-white">
        <Container>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16"
          >
            {services.map((service, i) => (
              <motion.div key={i} variants={fadeInUp} className="group">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-mineral text-amber group-hover:bg-amber group-hover:text-warm-white transition-colors duration-500">
                    <service.icon width={24} height={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-display font-semibold text-charcoal mb-3">
                      {service.title}
                    </h2>
                    <p className="text-base text-graphite leading-relaxed mb-6">
                      {service.desc}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-3 text-sm text-graphite-light">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber/50" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      <section className="py-20 lg:py-28 bg-mineral relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-charcoal/5 -skew-x-12 transform origin-top-right"></div>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <span className="text-sm font-bold tracking-widest text-amber uppercase mb-2 block">
                {t("Opérations Terrain", "Field Operations")}
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-semibold text-charcoal leading-tight">
                {t("Comptages Trafic & Enquêtes Origine-Destination", "Traffic Counts & OD Surveys")}
              </h2>
              <p className="text-graphite leading-relaxed text-lg">
                {t(
                  "En tant que partenaire expert et rigoureux, AVISIUM prend en charge la conception scientifique, le déploiement technologique et humain, ainsi que le post-traitement analytique de vos données de mobilité complexes.",
                  "As an expert and rigorous partner, AVISIUM handles the scientific design, technological and human deployment, and analytical post-processing of your complex mobility data."
                )}
              </p>
              <ul className="space-y-3 pt-2">
                {[
                  t("Comptages Routiers & Mouvements Tournants", "Road & Turning Movement Counts"),
                  t("Campagnes de terrain méthodologiques à grande échelle", "Large-scale methodological field campaigns"),
                  t("Audits Stationnement & Logistique Urbaine", "Parking & Urban Logistics Audits"),
                  t("Collecte de données vidéo par IA", "AI Video Data Collection")
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 mt-0.5 rounded-full bg-amber/20 flex items-center justify-center shrink-0">
                      <span className="w-2 h-2 rounded-full bg-amber" />
                    </span>
                    <span className="text-graphite-dark font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-charcoal text-white rounded-full font-medium hover:bg-amber-dark transition-all duration-300 group shadow-md hover:shadow-lg"
                >
                  {t("Demander une campagne de comptage", "Request a counting campaign")}
                  <NavArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="relative h-[450px] lg:h-[550px] w-full rounded-2xl overflow-hidden shadow-2xl border-4 border-white/50"
            >
              <img 
                src="/images/services/WhatsApp Image 2026-03-16 at 02.15.53 (17).jpeg"
                alt="Enquêtes Origine-Destination et Comptages AVISIUM"
                className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-transparent to-transparent flex items-end">
                <div className="p-8 w-full">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 p-5 rounded-xl shadow-lg">
                    <p className="text-white text-sm font-medium leading-relaxed">
                      {t("Une classification granulaire et une fiabilité 100% auditable des flux sur le terrain.", "Granular classification and 100% auditable flow reliability in the field.")}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>
    </>
  );
}
